// src/services/userProvisioningAPI.ts
// ✅ UPDATED: Cognito-native user provisioning (no DynamoDB invitations)

import { authService } from './authService';
import { logger } from '../utils/logger';

const userProvisioningLogger = logger.createServiceLogger('UserProvisioning');

// Types for user provisioning
export interface InviteAdminUserRequest {
  tenantId: string;
  email: string;
  firstName: string;
  lastName: string;
 role: 'OrgAdmin' | 'InventoryManager' | 'QuickAssign';
  organizationName?: string;
}

export interface TenantUser {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string; // FORCE_CHANGE_PASSWORD, CONFIRMED, etc.
  enabled: boolean;
  createdDate: string;
  lastModifiedDate: string;
}

export interface UserProvisioningAPIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

export interface InviteAdminResponse {
  message: string;
  userStatus: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tenantId: string;
  organizationName: string;
  isResend?: boolean;
}

export interface TenantUsersResponse {
  users: TenantUser[];
}

export interface ResendInvitationRequest {
  username: string;
  tenantId: string;
}

export interface UserActionRequest {
  username: string;
  tenantId: string;
}

class UserProvisioningAPI {
  private baseURL: string;

  constructor() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    if (!apiUrl) {
      throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    this.baseURL = apiUrl;
  }

  /**
   * Make authenticated request using centralized authService
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<UserProvisioningAPIResponse<T>> {
    const timer = logger.startTimer('UserProvisioning', 'makeRequest');
    
    userProvisioningLogger.info('Making user provisioning API request', {
      endpoint: `${this.baseURL}${endpoint}`,
      method: options.method || 'GET',
      hasBody: !!options.body,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await authService.makeAuthenticatedRequest(
        `${this.baseURL}${endpoint}`,
        options
      );

      userProvisioningLogger.info('API response received', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        method: options.method || 'GET'
      });

      if (!response.ok) {
        const responseText = await response.text();
        userProvisioningLogger.error('API request failed', {
          endpoint,
          method: options.method || 'GET',
          status: response.status,
          statusText: response.statusText,
          responseText: responseText.substring(0, 500)
        });
        
        timer();
        return {
          success: false,
          error: {
            code: 'HTTP_ERROR',
            message: `HTTP ${response.status}: ${response.statusText} - ${responseText}`
          }
        };
      }

      const data = await response.json();
      userProvisioningLogger.info('API request completed successfully', {
        endpoint,
        method: options.method || 'GET',
        hasData: !!data,
        dataKeys: data ? Object.keys(data) : []
      });
      
      timer();
      return {
        success: true,
        data: data.data || data
      };
    } catch (error: any) {
      timer();
      
      userProvisioningLogger.error('API request error', {
        endpoint,
        method: options.method || 'GET',
        error: error.message,
        errorCode: error.code,
        errorName: error.name
      }, error);
      
      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' }
        };
      }

      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' }
        };
      }

      if (error.code === 'NETWORK_ERROR') {
        return {
          success: false,
          error: { code: 'NETWORK_ERROR', message: error.message }
        };
      }

      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error.message || 'Unknown error occurred'
        }
      };
    }
  }

  /**
   * Invite a user for a tenant
   */
  async inviteAdminUser(
    inviteData: InviteAdminUserRequest
  ): Promise<UserProvisioningAPIResponse<InviteAdminResponse>> {
    userProvisioningLogger.info('Initiating user invitation', {
      tenantId: inviteData.tenantId,
      email: inviteData.email,
      firstName: inviteData.firstName,
      lastName: inviteData.lastName,
      role: inviteData.role,
      organizationName: inviteData.organizationName,
      timestamp: new Date().toISOString()
    });
    
    // Validate input
    const errors = this.validateInviteData(inviteData);
    if (errors.length > 0) {
      userProvisioningLogger.warn('Invitation data validation failed', {
        tenantId: inviteData.tenantId,
        email: inviteData.email,
        validationErrors: errors
      });
      
      return {
        success: false,
        error: {
          code: 'VALIDATION_ERROR',
          message: errors.join(', ')
        }
      };
    }
    
    return this.makeRequest<InviteAdminResponse>('/user-provisioning/invite-admin', {
      method: 'POST',
      body: JSON.stringify(inviteData)
    });
  }

  /**
   * Get all users for a tenant (from Cognito)
   */
  async getTenantUsers(tenantId: string): Promise<UserProvisioningAPIResponse<TenantUsersResponse>> {
    userProvisioningLogger.info('Retrieving tenant users', {
      tenantId,
      timestamp: new Date().toISOString()
    });
    
    return this.makeRequest<TenantUsersResponse>(`/user-provisioning/tenant-users/${tenantId}`);
  }

  /**
   * Resend invitation to user (for users in FORCE_CHANGE_PASSWORD state)
   */
  async resendInvitation(
    username: string, 
    tenantId: string
  ): Promise<UserProvisioningAPIResponse<{ message: string }>> {
    userProvisioningLogger.info('Resending user invitation', {
      username,
      tenantId,
      action: 'resend',
      timestamp: new Date().toISOString()
    });
    
    return this.makeRequest<{ message: string }>(`/user-provisioning/resend-invitation/${username}`, {
      method: 'POST',
      body: JSON.stringify({ tenantId })
    });
  }

  /**
   * Disable user
   */
  async disableUser(
    username: string,
    tenantId: string
  ): Promise<UserProvisioningAPIResponse<{ message: string }>> {
    userProvisioningLogger.info('Disabling user', {
      username,
      tenantId,
      action: 'disable',
      timestamp: new Date().toISOString()
    });
    
    return this.makeRequest<{ message: string }>(`/user-provisioning/disable-user/${username}`, {
      method: 'PUT',
      body: JSON.stringify({ tenantId })
    });
  }

  /**
   * Enable user
   */
  async enableUser(
    username: string,
    tenantId: string
  ): Promise<UserProvisioningAPIResponse<{ message: string }>> {
    userProvisioningLogger.info('Enabling user', {
      username,
      tenantId,
      action: 'enable',
      timestamp: new Date().toISOString()
    });
    
    return this.makeRequest<{ message: string }>(`/user-provisioning/enable-user/${username}`, {
      method: 'PUT',
      body: JSON.stringify({ tenantId })
    });
  }

  /**
   * Validate invitation data
   */
  private validateInviteData(data: InviteAdminUserRequest): string[] {
    const errors: string[] = [];

    userProvisioningLogger.debug('Validating invitation data', {
      tenantId: data.tenantId,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      role: data.role,
      hasAllRequiredFields: !!(data.tenantId && data.email && data.firstName && data.lastName && data.role)
    });

    if (!data.tenantId?.trim()) {
      errors.push('Tenant ID is required');
    }

    if (!data.email?.trim()) {
      errors.push('Email address is required');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (data.email && !emailRegex.test(data.email)) {
      errors.push('Invalid email format');
    }

    if (!data.firstName?.trim()) {
      errors.push('First name is required');
    }

    if (!data.lastName?.trim()) {
      errors.push('Last name is required');
    }

   if (data.role && !['OrgAdmin', 'InventoryManager', 'QuickAssign'].includes(data.role)) {
      errors.push('Invalid role - must be OrgAdmin or InventoryManager');
    }

    if (errors.length > 0) {
      userProvisioningLogger.warn('Invitation data validation completed with errors', {
        tenantId: data.tenantId,
        email: data.email,
        errorCount: errors.length,
        errors
      });
    } else {
      userProvisioningLogger.debug('Invitation data validation passed', {
        tenantId: data.tenantId,
        email: data.email,
        role: data.role
      });
    }

    return errors;
  }

  /**
   * Check if email is valid format
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    
    userProvisioningLogger.debug('Email format validation', {
      email: email.substring(0, 20) + '...',
      isValid,
      emailLength: email.length
    });
    
    return isValid;
  }

  /**
   * Format Cognito user status for display
   */
  formatUserStatus(status: string): { label: string; color: string } {
    const statusMap: Record<string, { label: string; color: string }> = {
      'FORCE_CHANGE_PASSWORD': { 
        label: 'Invitation Sent', 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200' 
      },
      'CONFIRMED': { 
        label: 'Active', 
        color: 'bg-green-100 text-green-800 border-green-200' 
      },
      'UNCONFIRMED': { 
        label: 'Pending Verification', 
        color: 'bg-blue-100 text-blue-800 border-blue-200' 
      },
      'ARCHIVED': { 
        label: 'Archived', 
        color: 'bg-gray-100 text-gray-800 border-gray-200' 
      },
      'COMPROMISED': { 
        label: 'Compromised', 
        color: 'bg-red-100 text-red-800 border-red-200' 
      },
      'UNKNOWN': { 
        label: 'Unknown', 
        color: 'bg-gray-100 text-gray-800 border-gray-200' 
      },
      'RESET_REQUIRED': { 
        label: 'Reset Required', 
        color: 'bg-orange-100 text-orange-800 border-orange-200' 
      }
    };

    const result = statusMap[status] || 
                  { label: status, color: 'bg-gray-100 text-gray-800 border-gray-200' };

    userProvisioningLogger.debug('User status formatted', {
      originalStatus: status,
      formattedLabel: result.label,
      hasCustomColor: status in statusMap
    });

    return result;
  }

  /**
   * Check if user can have invitation resent
   */
  canResendInvitation(status: string): boolean {
    const resendableStatuses = ['FORCE_CHANGE_PASSWORD'];
    const canResend = resendableStatuses.includes(status);
    
    userProvisioningLogger.debug('Invitation resend check', {
      status,
      canResend,
      resendableStatuses
    });
    
    return canResend;
  }

  /**
   * Check if user is active
   */
  isUserActive(status: string): boolean {
    const isActive = status === 'CONFIRMED';
    
    userProvisioningLogger.debug('User active status check', {
      status,
      isActive
    });
    
    return isActive;
  }
}

// Export singleton instance
export const userProvisioningAPI = new UserProvisioningAPI();
